import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { ArrowLeft, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import SignatureCapture from "../components/SignatureCapture";
import DocumentsUpload from "../components/DocumentsUpload";

const initialForm = {
  // Personal
  full_name: "",
  age: "",
  gender: "",
  phone: "",
  blood_group: "",
  // Addresses
  local_address: "",
  permanent_address: "",
  same_as_local: false,
  // Education
  current_college: "",
  current_semester: "",
  education_qualifications: "",
  applicant_status: "",
  // Course
  course_id: "",
  // Contacts
  guardian_name: "",
  guardian_phone: "",
  emergency_name: "",
  emergency_phone: "",
  // Signature
  signature_base64: null,
  // Documents (transcripts, ID proof)
  documents: [],
};

const STEPS = ["Personal", "Education", "Course", "Sign-off"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

export default function Register() {
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(0);
  const [courses, setCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedCourse = useMemo(
    () => courses.find((c) => c.id === form.course_id) || null,
    [courses, form.course_id]
  );

  const validateStep = () => {
    if (step === 0) {
      const req = ["full_name", "age", "gender", "phone", "blood_group", "local_address"];
      for (const k of req) if (!String(form[k]).trim()) return `Please fill ${k.replace(/_/g, " ")}`;
      const ageNum = Number(form.age);
      if (!Number.isInteger(ageNum) || ageNum < 5 || ageNum > 120) return "Please enter a valid age";
      if (!form.same_as_local && !String(form.permanent_address).trim()) return "Please fill permanent address";
    }
    if (step === 1) {
      const req = ["current_college", "current_semester", "education_qualifications", "applicant_status"];
      for (const k of req) if (!String(form[k]).trim()) return `Please fill ${k.replace(/_/g, " ")}`;
    }
    if (step === 2) {
      if (!form.course_id) return "Please select a course";
    }
    if (step === 3) {
      const req = ["guardian_name", "guardian_phone", "emergency_name", "emergency_phone"];
      for (const k of req) if (!String(form[k]).trim()) return `Please fill ${k.replace(/_/g, " ")}`;
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) {
      toast.error(err);
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const submit = async () => {
    const err = validateStep();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age),
        permanent_address: form.same_as_local ? form.local_address : form.permanent_address,
      };
      const { data } = await api.post("/registrations", payload);
      toast.success("Application submitted!");
      navigate("/register/success", { state: { reg: data } });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grid md:grid-cols-2">
      {/* Left visual */}
      <div className="hidden md:block relative h-screen sticky top-0 overflow-hidden border-r border-slate-200">
        <img
          src="https://images.pexels.com/photos/12091126/pexels-photo-12091126.jpeg"
          alt="Campus"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative h-full p-12 flex flex-col text-white">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-2 font-mono-sms text-xs uppercase tracking-[0.2em] opacity-80 hover:opacity-100"
          >
            <ArrowLeft size={14} weight="bold" /> Back home
          </Link>
          <div className="mt-auto">
            <div className="font-mono-sms text-xs uppercase tracking-[0.3em] mb-4 opacity-80">
              Phase 01 · Cohort 2026
            </div>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-[0.95] mb-6">
              Begin your
              <br />
              application.
            </h1>
            <p className="text-base opacity-80 max-w-md">
              Fill out personal details, choose your programme, and provide guardian info. You'll receive your Student ID once approved.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="px-6 md:px-12 py-10 md:py-16 sms-scroll overflow-y-auto">
        <div className="max-w-xl">
          {/* Step indicator */}
          <div className="flex items-center gap-3 mb-10">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-3">
                <div
                  className={`w-7 h-7 flex items-center justify-center text-xs font-mono-sms font-bold border ${
                    i <= step
                      ? "bg-slate-900 border-slate-900 text-white"
                      : "bg-white border-slate-300 text-slate-400"
                  }`}
                  data-testid={`step-indicator-${i}`}
                >
                  {i + 1}
                </div>
                <div className={`font-mono-sms text-[11px] uppercase tracking-[0.2em] ${i === step ? "text-slate-900" : "text-slate-400"}`}>
                  {label}
                </div>
                {i < STEPS.length - 1 && <div className="w-8 h-px bg-slate-300" />}
              </div>
            ))}
          </div>

          <h2 className="font-display text-4xl font-black tracking-tighter mb-2">
            {step === 0 && "Personal details"}
            {step === 1 && "Education background"}
            {step === 2 && "Pick your course"}
            {step === 3 && "Contacts & sign-off"}
          </h2>
          <p className="text-slate-500 text-sm mb-10">
            {step === 0 && "Identification, contact, and where you live."}
            {step === 1 && "Your current studies and academic status."}
            {step === 2 && "Select the programme you wish to enrol in. Fees auto-fill from the catalog."}
            {step === 3 && "Guardian, emergency contact, and your signature."}
          </p>

          {step === 0 && (
            <div className="space-y-5">
              <Field label="Full name" testid="field-full_name">
                <input className="sms-input" data-testid="input-full_name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} />
              </Field>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Age" testid="field-age">
                  <input className="sms-input" type="number" min={5} max={120} data-testid="input-age" value={form.age} onChange={(e) => update("age", e.target.value)} />
                </Field>
                <Field label="Gender" testid="field-gender">
                  <select className="sms-input" data-testid="input-gender" value={form.gender} onChange={(e) => update("gender", e.target.value)}>
                    <option value="">Select…</option>
                    <option>Male</option>
                    <option>Female</option>
                  </select>
                </Field>
                <Field label="Blood group" testid="field-blood_group">
                  <select className="sms-input" data-testid="input-blood_group" value={form.blood_group} onChange={(e) => update("blood_group", e.target.value)}>
                    <option value="">Select…</option>
                    {BLOOD_GROUPS.map((b) => <option key={b}>{b}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Phone number" testid="field-phone">
                <input className="sms-input" data-testid="input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
              </Field>
              <Field label="Local address" testid="field-local_address">
                <textarea rows={2} className="sms-input" data-testid="input-local_address" value={form.local_address} onChange={(e) => update("local_address", e.target.value)} />
              </Field>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="field-same_as_local">
                <input type="checkbox" data-testid="input-same_as_local" checked={form.same_as_local} onChange={(e) => update("same_as_local", e.target.checked)} className="w-4 h-4 accent-[var(--sms-primary)]" />
                <span className="text-sm text-slate-700">Permanent address is the same as local address</span>
              </label>
              {!form.same_as_local && (
                <Field label="Permanent address" testid="field-permanent_address">
                  <textarea rows={2} className="sms-input" data-testid="input-permanent_address" value={form.permanent_address} onChange={(e) => update("permanent_address", e.target.value)} />
                </Field>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Current college" testid="field-current_college">
                  <input className="sms-input" data-testid="input-current_college" value={form.current_college} onChange={(e) => update("current_college", e.target.value)} />
                </Field>
                <Field label="Current semester / year" testid="field-current_semester">
                  <input className="sms-input" data-testid="input-current_semester" placeholder="e.g. 4th sem · Final year" value={form.current_semester} onChange={(e) => update("current_semester", e.target.value)} />
                </Field>
              </div>
              <Field label="Education qualifications" testid="field-education_qualifications">
                <textarea rows={4} className="sms-input" data-testid="input-education_qualifications" placeholder="e.g. 10th: 95%, 12th: 88%, BSc CS: 8.2 CGPA" value={form.education_qualifications} onChange={(e) => update("education_qualifications", e.target.value)} />
              </Field>
              <Field label="Status" testid="field-applicant_status">
                <select className="sms-input" data-testid="input-applicant_status" value={form.applicant_status} onChange={(e) => update("applicant_status", e.target.value)}>
                  <option value="">Select…</option>
                  <option>Fresher</option>
                  <option>Passout</option>
                </select>
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="space-y-3" data-testid="course-list">
                {courses.length === 0 && (
                  <div className="text-sm text-slate-500">Loading courses…</div>
                )}
                {courses.map((c) => (
                  <label
                    key={c.id}
                    data-testid={`course-option-${c.code}`}
                    className={`block sms-card p-5 cursor-pointer transition-all ${
                      form.course_id === c.id ? "border-[var(--sms-primary)] ring-1 ring-[var(--sms-primary)]" : "sms-card-hoverable"
                    }`}
                  >
                    <input
                      type="radio"
                      name="course"
                      className="sr-only"
                      checked={form.course_id === c.id}
                      onChange={() => update("course_id", c.id)}
                    />
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-display text-lg font-bold tracking-tight">{c.name}</div>
                        <div className="text-sm text-slate-500 mt-1">{c.description}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono-sms text-xs uppercase tracking-[0.15em] text-[var(--sms-primary)]">{c.code}</div>
                        <div className="font-mono-sms text-[10px] text-slate-400 mt-1">{c.duration_days} {c.duration_days === 1 ? "day" : "days"}</div>
                        <div className="font-mono-sms text-sm text-slate-900 mt-2">₹{Number(c.fee_amount || 0).toLocaleString()}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <Field label="Payment amount (auto-filled)" testid="field-payment_amount">
                <input
                  className="sms-input bg-slate-100 text-slate-700"
                  data-testid="input-payment_amount"
                  readOnly
                  value={selectedCourse ? `₹ ${Number(selectedCourse.fee_amount || 0).toLocaleString()}` : "Select a course to see fees"}
                />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-3">Guardian</div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Name" testid="field-guardian_name">
                    <input className="sms-input" data-testid="input-guardian_name" value={form.guardian_name} onChange={(e) => update("guardian_name", e.target.value)} />
                  </Field>
                  <Field label="Phone" testid="field-guardian_phone">
                    <input className="sms-input" data-testid="input-guardian_phone" value={form.guardian_phone} onChange={(e) => update("guardian_phone", e.target.value)} />
                  </Field>
                </div>
              </div>
              <div>
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-3">Emergency contact</div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Name" testid="field-emergency_name">
                    <input className="sms-input" data-testid="input-emergency_name" value={form.emergency_name} onChange={(e) => update("emergency_name", e.target.value)} />
                  </Field>
                  <Field label="Phone" testid="field-emergency_phone">
                    <input className="sms-input" data-testid="input-emergency_phone" value={form.emergency_phone} onChange={(e) => update("emergency_phone", e.target.value)} />
                  </Field>
                </div>
              </div>
              <div>
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-3">Signature</div>
                <SignatureCapture
                  value={form.signature_base64}
                  onChange={(b64) => update("signature_base64", b64)}
                />
              </div>
              <div>
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-3">Documents</div>
                <DocumentsUpload
                  value={form.documents}
                  onChange={(docs) => update("documents", docs)}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-200">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="btn-back"
              className="sms-btn-secondary disabled:opacity-40"
            >
              Back
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} data-testid="btn-next" className="sms-btn-primary inline-flex items-center gap-2">
                Continue <ArrowRight size={16} weight="bold" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} data-testid="btn-submit" className="sms-btn-primary inline-flex items-center gap-2">
                {submitting ? "Submitting…" : (<><CheckCircle size={16} weight="bold" /> Submit application</>)}
              </button>
            )}
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}
